import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
    Container,
    Paper,
    Tabs,
    Tab,
    Box,
    Typography,
    Button,
    CircularProgress
} from "@mui/material";
import {
    Language as WebIcon,
    Add as AddIcon,
    History as HistoryIcon,
    ViewList as ListIcon,
    Schedule as ScheduleIcon
} from "@mui/icons-material";
import * as Actions from "actions";
import WebsiteScanForm from "components/elements/admin/website-scraper/WebsiteScanForm";
import ScanProgressList from "components/elements/admin/website-scraper/ScanProgressList";
import ScrapedProductsList from "components/elements/admin/website-scraper/ScrapedProductsList";
import DomainsList from "components/elements/admin/website-scraper/DomainsList";
import ScheduledScanDialog from "components/elements/admin/website-scraper/ScheduledScanDialog";
import PriceChangesView from "components/elements/admin/website-scraper/PriceChangesView";

const AdminWebsiteScraperMain = (props) => {
    const [activeTab, setActiveTab] = useState(0);
    const [showScanForm, setShowScanForm] = useState(false);
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [scheduleDomain, setScheduleDomain] = useState(null);

    const {
        scannedDomains,
        activeScans,
        scrapedProducts,
        productsPagination,
        loading,
        adminGetScannedDomains,
        adminStartWebsiteScan,
        adminQuickScan,
        adminGetScanStatus,
        adminGetScrapedProducts,
        adminCancelScan,
        adminDeleteScrapedProducts,
        adminGetScanHistory
    } = props;

    useEffect(() => {
        adminGetScannedDomains();
    }, []);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleOpenScanForm = () => {
        setShowScanForm(true);
    };

    const handleCloseScanForm = () => {
        setShowScanForm(false);
    };

    const handleStartScan = (url, config) => {
        adminStartWebsiteScan(url, config, (err, data) => {
            if (!err) {
                handleCloseScanForm();
                setActiveTab(1);
            }
        });
    };

    const handleQuickScan = (url, callback) => {
        adminQuickScan(url, callback);
    };

    const handleSelectDomain = (domain) => {
        setSelectedDomain(domain);
        adminGetScrapedProducts(domain, { limit: 50, skip: 0 });
        setActiveTab(2);
    };

    const handleRefreshDomains = () => {
        adminGetScannedDomains();
    };

    const handleLoadProducts = (domain, options) => {
        adminGetScrapedProducts(domain, options);
    };

    const handleDeleteDomain = (domain) => {
        if (window.confirm(`Are you sure you want to delete all products from ${domain}?`)) {
            adminDeleteScrapedProducts(domain, (err) => {
                if (!err) {
                    adminGetScannedDomains();
                    if (selectedDomain === domain) {
                        setSelectedDomain(null);
                    }
                }
            });
        }
    };

    const handleViewScanHistory = (domain) => {
        adminGetScanHistory(domain, 10);
        setActiveTab(3);
    };

    const handleOpenScheduleDialog = (domain) => {
        setScheduleDomain(domain);
        setShowScheduleDialog(true);
    };

    const handleCloseScheduleDialog = () => {
        setShowScheduleDialog(false);
        setScheduleDomain(null);
    };

    const handleSaveScheduledScan = (domain, config) => {
        // This would call the API - for now just close dialog
        // TODO: Implement the API call once the action is created
        console.log('Save scheduled scan:', domain, config);
        handleCloseScheduleDialog();
        alert('Scheduled scan configured successfully!');
    };

    const handleViewPriceChanges = (domain) => {
        setSelectedDomain(domain);
        setActiveTab(3);
    };

    const renderHeader = () => {
        return (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Website Product Scraper
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Scan ecommerce websites and extract product data with structured formats
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenScanForm}
                    size="large"
                >
                    New Scan
                </Button>
            </Box>
        );
    };

    const renderTabs = () => {
        return (
            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                >
                    <Tab icon={<WebIcon />} label="Domains" />
                    <Tab icon={<HistoryIcon />} label="Active Scans" />
                    <Tab icon={<ListIcon />} label="Products" />
                    <Tab icon={<ScheduleIcon />} label="Price Changes" />
                </Tabs>
            </Paper>
        );
    };

    const renderContent = () => {
        switch (activeTab) {
            case 0:
                return (
                    <DomainsList
                        domains={scannedDomains}
                        loading={loading}
                        onSelectDomain={handleSelectDomain}
                        onRefresh={handleRefreshDomains}
                        onDeleteDomain={handleDeleteDomain}
                        onViewHistory={handleViewScanHistory}
                        onConfigureSchedule={handleOpenScheduleDialog}
                        onViewPriceChanges={handleViewPriceChanges}
                    />
                );
            
            case 1:
                return (
                    <ScanProgressList
                        scans={activeScans}
                        loading={loading}
                        onRefreshScan={adminGetScanStatus}
                        onCancelScan={adminCancelScan}
                        onViewProducts={handleSelectDomain}
                    />
                );
            
            case 2:
                return (
                    <ScrapedProductsList
                        products={scrapedProducts}
                        pagination={productsPagination}
                        selectedDomain={selectedDomain}
                        loading={loading}
                        onLoadProducts={handleLoadProducts}
                        onSelectDomain={setSelectedDomain}
                        domains={scannedDomains}
                    />
                );
            
            case 3:
                return selectedDomain ? (
                    <PriceChangesView
                        domain={selectedDomain}
                        onLoadPriceChanges={(domain, days, callback) => {
                            // TODO: Implement API call
                            callback(null, []);
                        }}
                        onLoadPriceHistory={(productId, limit, callback) => {
                            // TODO: Implement API call
                            callback(null, []);
                        }}
                    />
                ) : (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="textSecondary">
                            Select a domain to view price changes
                        </Typography>
                    </Paper>
                );
            
            default:
                return null;
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {renderHeader()}
            {renderTabs()}
            {renderContent()}
            
            <WebsiteScanForm
                open={showScanForm}
                onClose={handleCloseScanForm}
                onStartScan={handleStartScan}
                onQuickScan={handleQuickScan}
                loading={loading}
            />

            <ScheduledScanDialog
                open={showScheduleDialog}
                onClose={handleCloseScheduleDialog}
                onSave={handleSaveScheduledScan}
                domain={scheduleDomain}
                loading={loading}
            />
        </Container>
    );
};

const mapStateToProps = (state) => ({
    scannedDomains: state.adminWebsiteScraper?.scannedDomains || [],
    activeScans: state.adminWebsiteScraper?.activeScans || [],
    scrapedProducts: state.adminWebsiteScraper?.scrapedProducts || [],
    productsPagination: state.adminWebsiteScraper?.productsPagination || { total: 0, page: 1, limit: 50, total_pages: 0 },
    loading: state.adminWebsiteScraper?.loading || false,
    error: state.adminWebsiteScraper?.error || null
});

const mapDispatchToProps = (dispatch) => 
    bindActionCreators(
        {
            adminGetScannedDomains: Actions.adminGetScannedDomains,
            adminStartWebsiteScan: Actions.adminStartWebsiteScan,
            adminQuickScan: Actions.adminQuickScan,
            adminGetScanStatus: Actions.adminGetScanStatus,
            adminGetScrapedProducts: Actions.adminGetScrapedProducts,
            adminCancelScan: Actions.adminCancelScan,
            adminDeleteScrapedProducts: Actions.adminDeleteScrapedProducts,
            adminGetScanHistory: Actions.adminGetScanHistory
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(AdminWebsiteScraperMain);
